/**
 * wishlist.js
 * Place in the theme's assets/ folder. Loaded by snippets/wishlist-init.liquid,
 * which also sets window.WishlistConfig.
 *
 * Favouriting is at variant (case-size) level: the specific case size a
 * customer had selected is what gets saved, not just "this wine".
 *
 * Login is required - there's no guest/localStorage wishlist any more (it
 * couldn't follow a customer across devices, which defeats the point).
 * Variant IDs live in a Cloudflare KV entry via the wishlist-worker,
 * HMAC-authenticated against the customer's ID - never their email - not a
 * Shopify metafield, and not any price/inventory data.
 *
 * A logged-out click on the Add to Wishlist button redirects to
 * /account/login?return_to=<this product, with ?variant= and &wishlist_add=
 * set>, so classic customer accounts land the customer straight back on this
 * exact variant once they're signed in; init() then finishes the add they
 * started and strips wishlist_add from the URL. This needs a `return_to`
 * hidden field in the theme's customer_login form (Dawn and most themes
 * already have this - see the README if a custom login template doesn't).
 *
 * Grower/region/country/case size/price/availability are never stored
 * anywhere by this feature - they're read fresh, live, from the Shopify
 * Admin API (via the worker's /wishlist-items route) at render time on the
 * wishlist page, scoped to just the saved variant IDs.
 */
(function () {
  'use strict';

  var WISHLIST_ADD_PARAM = 'wishlist_add';
  var config = window.WishlistConfig || {};
  var currentIds = []; // in-memory cache for the current page load

  // -- worker calls --------------------------------------------------------

  function customerAuthParams() {
    return {
      store: config.store,
      customerId: config.customerId,
      ts: config.authTs,
      token: config.authToken,
    };
  }

  function fetchCustomerIds() {
    var params = customerAuthParams();
    var qs = Object.keys(params)
      .map(function (k) { return encodeURIComponent(k) + '=' + encodeURIComponent(params[k]); })
      .join('&');
    return fetch(config.workerUrl + '/customer-wishlist?' + qs)
      .then(function (res) { return res.ok ? res.json() : { variantIds: [] }; })
      .then(function (data) { return data.variantIds || []; })
      .catch(function () { return []; });
  }

  function postCustomerAction(body) {
    var payload = Object.assign({}, customerAuthParams(), body);
    return fetch(config.workerUrl + '/customer-wishlist', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) { return (data && data.variantIds) || null; })
      .catch(function () { return null; });
  }

  // -- public API ------------------------------------------------------------

  function isWishlisted(variantId) {
    return currentIds.indexOf(String(variantId)) !== -1;
  }

  // Only ever called for a logged-in customer - a logged-out click on the
  // button is intercepted in wireButtons() and redirected to login instead.
  function toggle(variantId) {
    variantId = String(variantId);
    var wasWishlisted = isWishlisted(variantId);

    // Optimistic UI update.
    if (wasWishlisted) {
      currentIds = currentIds.filter(function (id) { return id !== variantId; });
    } else {
      currentIds = currentIds.concat([variantId]);
    }
    syncUi();

    postCustomerAction({ action: wasWishlisted ? 'remove' : 'add', variantId: variantId }).then(function (serverIds) {
      if (serverIds) currentIds = serverIds;
      syncUi();
    });
  }

  function remove(variantId) {
    if (isWishlisted(variantId)) toggle(variantId);
  }

  // Points every wishlist button on the page at a different variant (used
  // when the customer changes case size) and re-syncs the UI to match.
  function setActiveVariant(variantId) {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      btn.setAttribute('data-wishlist-variant-id', String(variantId));
    });
    syncUi();
  }

  // Sends a logged-out visitor to log in, then straight back to this exact
  // product with this exact variant preselected, with a flag telling init()
  // to finish the add once they're back. Relies on the theme's
  // customer_login form having a `return_to` hidden field (see README) -
  // that's what Shopify's classic accounts use to redirect post-login.
  function redirectToLoginThenAdd(variantId) {
    var url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    url.searchParams.set(WISHLIST_ADD_PARAM, variantId);
    var returnTo = url.pathname + url.search;
    window.location.href = '/account/login?return_to=' + encodeURIComponent(returnTo);
  }

  // Picks up after a redirectToLoginThenAdd() round trip: finishes the add
  // the customer started before they were sent to log in, then strips the
  // flag from the URL so it doesn't fire again on refresh/back button.
  function completePendingWishlistAdd() {
    var params = new URLSearchParams(window.location.search);
    var pendingId = params.get(WISHLIST_ADD_PARAM);
    if (!pendingId) return;

    if (!isWishlisted(pendingId)) toggle(pendingId);

    params.delete(WISHLIST_ADD_PARAM);
    var newSearch = params.toString();
    var newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
    window.history.replaceState({}, '', newUrl);
  }

  // -- UI sync ---------------------------------------------------------------

  function syncUi() {
    var countEls = document.querySelectorAll('[data-wishlist-count]');
    countEls.forEach(function (el) {
      el.textContent = String(currentIds.length);
      if (currentIds.length > 0) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });

    var buttons = document.querySelectorAll('[data-wishlist-toggle]');
    buttons.forEach(function (btn) {
      var id = btn.getAttribute('data-wishlist-variant-id');
      var active = isWishlisted(id);
      btn.classList.toggle('is-wishlisted', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      var label = btn.querySelector('[data-wishlist-label]');
      if (label) label.textContent = active ? 'Remove from Wishlist' : 'Add to Wishlist';
    });

    if (document.querySelector('[data-wishlist-page]')) renderWishlistPage();
  }

  function wireButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      if (btn.__wlWired) return;
      btn.__wlWired = true;
      btn.addEventListener('click', function () {
        var variantId = btn.getAttribute('data-wishlist-variant-id');
        if (!config.loggedIn) {
          redirectToLoginThenAdd(variantId);
          return;
        }
        toggle(variantId);
      });
    });
  }

  // Keeps the button(s) pointed at whichever case size is currently
  // selected. Listens at the document level rather than scoping to
  // btn.closest('form') - on this theme the wishlist button isn't nested
  // inside the product form at all, and Dawn's variant-radios/variant-selects
  // dispatch a bubbling native 'change' event on input[name="id"] regardless
  // of where else on the page a listener sits, so delegation here is both
  // simpler and correct for themes where the two aren't nested together.
  document.addEventListener('change', function (e) {
    if (e.target && e.target.name === 'id' && e.target.value) {
      setActiveVariant(e.target.value);
    }
  });

  // Some themes broadcast variant changes as a custom event instead of (or
  // as well as) updating a plain name="id" field. Covers that convention
  // too, in case e.detail.variant.id (or e.detail.id) is what's available.
  document.addEventListener('variant:change', function (e) {
    var detail = e.detail || {};
    var variant = detail.variant || detail;
    var variantId = variant && variant.id;
    if (variantId) setActiveVariant(variantId);
  });

  // -- duty status: Duty Paid vs In Bond --------------------------------------
  //
  // Business Central can't process an order that mixes Duty Paid and In Bond
  // items, so the basket is never allowed to hold both at once - the product
  // page enforces this via checkCartAndUpdateUI(), keyed off cart.requires_shipping
  // (true once any Duty Paid/shippable line is in the basket) versus each
  // radio's duty_free (In Bond)/duty_paid class. There's no radio here, so
  // the equivalent signal is the item's own requiresShipping, which the
  // worker now returns per variant (see VARIANT_ITEMS_QUERY in
  // wishlist-worker.js): true = Duty Paid, false = In Bond.

  function fetchCartDutyState() {
    return fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        return { isEmpty: cart.item_count === 0, requiresShipping: !!cart.requires_shipping };
      })
      .catch(function () {
        // Can't tell - don't block on a network hiccup. /cart/add will still
        // fail on Shopify/BC's own side if there's a genuine mismatch.
        return null;
      });
  }

  // An empty basket (or a failed /cart.js check) never blocks anything.
  function dutyConflict(cartState, item) {
    if (!cartState || cartState.isEmpty) return false;
    return !!cartState.requiresShipping !== !!item.requiresShipping;
  }

  function dutyWarningText(item) {
    return item.requiresShipping
      ? 'Sorry, your basket already has In-Bond wines in it - please complete this order before purchasing Duty Paid wines.'
      : 'Sorry, your basket already has Duty Paid wines in it - please complete this order before purchasing In-Bond wines.';
  }

  function dutyWarningEl(btn) {
    return btn.parentElement && btn.parentElement.querySelector('[data-wishlist-duty-warning]');
  }

  // Re-checks every rendered card's Add to Basket button against the
  // basket's current duty state - called after the page first renders and
  // again after any successful add, since that add may itself have just
  // locked the basket to one duty type.
  function applyDutyState(cartState) {
    document.querySelectorAll('[data-wishlist-add-basket]').forEach(function (btn) {
      var item = btn.__wlItem;
      if (!item || !item.available) return; // already disabled for another reason
      var blocked = dutyConflict(cartState, item);
      btn.disabled = blocked;
      var warning = dutyWarningEl(btn);
      if (warning) {
        warning.textContent = blocked ? dutyWarningText(item) : '';
        warning.hidden = !blocked;
      }
    });
  }

  // The heading note under each section is conditional on the basket's
  // current duty state too - it only appears on the section that's actually
  // locked out right now, not on both unconditionally, and says which type
  // is already in the basket. Hidden entirely on an empty basket.
  function applySectionNotes(cartState) {
    var dutyPaidNote = document.querySelector('[data-wishlist-section="duty_paid"] [data-wishlist-section-note]');
    var inBondNote = document.querySelector('[data-wishlist-section="in_bond"] [data-wishlist-section-note]');
    var basketHasItems = !!(cartState && !cartState.isEmpty);
    var basketIsDutyPaid = basketHasItems && cartState.requiresShipping;
    var basketIsInBond = basketHasItems && !cartState.requiresShipping;

    if (dutyPaidNote) {
      dutyPaidNote.hidden = !basketIsInBond;
      if (basketIsInBond) {
        dutyPaidNote.textContent = "Your basket already contains In Bond wines. We're sorry but Duty Paid and In Bond wines can't be combined in one order - please place a separate order for these wines.";
      }
    }
    if (inBondNote) {
      inBondNote.hidden = !basketIsDutyPaid;
      if (basketIsDutyPaid) {
        inBondNote.textContent = "Your basket already contains Duty Paid wines. We're sorry but Duty Paid and In Bond wines can't be combined in one order - please place a separate order for these wines.";
      }
    }
  }

  function refreshDutyState() {
    fetchCartDutyState().then(function (cartState) {
      applyDutyState(cartState);
      applySectionNotes(cartState);
    });
  }

  // Re-checks against a fresh /cart.js read right before actually adding -
  // covers the basket having changed (another tab, a product page add)
  // since the page last rendered - and only then calls addToBasket().
  function handleAddBasketClick(item, btn) {
    fetchCartDutyState().then(function (cartState) {
      var warning = dutyWarningEl(btn);
      if (dutyConflict(cartState, item)) {
        btn.disabled = true;
        if (warning) {
          warning.textContent = dutyWarningText(item);
          warning.hidden = false;
        }
        return;
      }
      if (warning) warning.hidden = true;
      addToBasket(item.id, btn);
    });
  }

  // -- wishlist page: fetch live item data for the saved variants ------------

  function formatPrice(amount, currency) {
    if (amount == null || isNaN(amount)) return '';
    var symbol = currency === 'GBP' ? '£' : (currency ? currency + ' ' : '£');
    return symbol + amount.toFixed(2);
  }

  // Looks up current title/grower/region/country/case size/price/availability
  // for exactly these variant IDs via the worker, which queries the Shopify
  // Admin API live - nothing cached, nothing stored, request size scales
  // with wishlist size rather than the whole catalogue. Resolves with an
  // id->item map. Never rejects - a failed lookup just yields an empty map,
  // so a card falls back to its "no longer available" state below.
  function fetchWishlistItems(ids) {
    return fetch(config.workerUrl + '/wishlist-items', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ store: config.store, variantIds: ids }),
    })
      .then(function (res) { return res.ok ? res.json() : { items: [] }; })
      .then(function (data) {
        var map = {};
        (data.items || []).forEach(function (item) { map[item.id] = item; });
        return map;
      })
      .catch(function () { return {}; });
  }

  // Reuses this theme's own cart-notification (Dawn) component rather than
  // reinventing cart feedback, so this behaves exactly like the native Add
  // to Basket button on a product page - same popup, same sections. Verified
  // directly against the live site: cart-notification exposes
  // getSectionsToRender()/renderContents(), and needs the sectioned
  // /cart/add endpoint (not /cart/add.js) to have data to render. Falls back
  // to a plain add with just the button's own text as feedback if neither
  // cart-notification nor cart-drawer is found on the page.
  function addToBasket(variantId, btn) {
    var originalLabel = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    var cartUi = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    if (cartUi && typeof cartUi.getSectionsToRender === 'function') {
      formData.append('sections', cartUi.getSectionsToRender().map(function (s) { return s.id; }).join(','));
      formData.append('sections_url', window.location.pathname);
    }

    fetch('/cart/add', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    })
      .then(function (res) { return res.json(); })
      .then(function (response) {
        if (response.status) throw new Error(response.description || 'add to cart failed');
        if (cartUi && typeof cartUi.renderContents === 'function') cartUi.renderContents(response);
        btn.textContent = 'Added ✓';
        // The basket may now be locked to this item's duty type for the
        // first time (or still empty, if this was a 0-qty edge case) -
        // re-check every card so a newly-mismatched one disables itself.
        refreshDutyState();
        setTimeout(function () { btn.textContent = originalLabel; btn.disabled = false; }, 1500);
      })
      .catch(function () {
        btn.textContent = 'Error - try again';
        setTimeout(function () { btn.textContent = originalLabel; btn.disabled = false; }, 1500);
      });
  }

  // Cards are grouped Duty Paid / In Bond / Unavailable - see the comment on
  // page-wishlist.liquid. "Unavailable" covers both a variant the worker
  // couldn't find at all (deleted, say) and one that's simply out of stock
  // (item.available === false) - neither can actually be ordered, so
  // grouping by duty state doesn't apply to either.
  var DUTY_SECTION_KEYS = ['duty_paid', 'in_bond', 'unavailable'];
  var CONTACT_EMAIL = 'info@honestgrapes.co.uk';

  // Swaps the Add to Basket button for a mailto Contact Us link - used for
  // anything that can't actually be ordered (out of stock, or deleted
  // entirely), so there's still a next step for the customer instead of a
  // dead disabled button. Runs on the still-detached template clone, so
  // replaceChild here operates on the fragment, not the live page.
  function contactLinkFor(btn) {
    var link = document.createElement('a');
    link.className = btn.className;
    link.href = 'mailto:' + CONTACT_EMAIL;
    link.textContent = 'Contact Us';
    btn.parentNode.replaceChild(link, btn);
    return link;
  }

  function dutySections() {
    var sections = {};
    DUTY_SECTION_KEYS.forEach(function (key) {
      sections[key] = {
        section: document.querySelector('[data-wishlist-section="' + key + '"]'),
        grid: document.querySelector('[data-wishlist-grid="' + key + '"]'),
      };
    });
    return sections;
  }

  function renderCardsFromFeed(feed) {
    var sections = dutySections();
    var template = document.getElementById('wl-card-template');
    if (!template) return;

    DUTY_SECTION_KEYS.forEach(function (key) {
      if (sections[key].grid) sections[key].grid.innerHTML = '';
    });

    currentIds.forEach(function (id) {
      var item = feed[id];
      var card = template.content.cloneNode(true);
      var imgLink = card.querySelector('.wl-card-image-link');
      var img = card.querySelector('.wl-card-image');
      var titleLink = card.querySelector('.wl-card-title');
      var meta = card.querySelector('.wl-card-meta');
      var price = card.querySelector('.wl-card-price');
      var availability = card.querySelector('.wl-card-availability');
      var addBasketBtn = card.querySelector('[data-wishlist-add-basket]');
      var removeBtn = card.querySelector('[data-wishlist-remove]');
      var sectionKey = 'unavailable';

      if (item) {
        imgLink.href = item.url;
        img.src = item.image;
        img.alt = item.title;
        titleLink.href = item.url;
        titleLink.textContent = item.title;
        meta.textContent = [item.grower, item.region, item.country, item.unitSize].filter(Boolean).join(' · ');
        price.textContent = formatPrice(item.price, item.currency);
        availability.textContent = item.available ? 'In stock' : 'Currently unavailable';
        availability.classList.toggle('wl-unavailable', !item.available);
        if (item.available) {
          addBasketBtn.__wlItem = item;
          addBasketBtn.addEventListener('click', function () { handleAddBasketClick(item, addBasketBtn); });
        } else {
          contactLinkFor(addBasketBtn);
        }
        // Out of stock can't be ordered either way, so it groups with
        // deleted variants rather than by duty state (see DUTY_SECTION_KEYS
        // comment above) - this is the fix for out-of-stock wines showing up
        // in the Duty Paid/In Bond sections instead of Unavailable.
        sectionKey = item.available ? (item.requiresShipping ? 'duty_paid' : 'in_bond') : 'unavailable';
      } else {
        titleLink.textContent = 'Item no longer available';
        titleLink.removeAttribute('href');
        meta.textContent = '';
        price.textContent = '';
        availability.textContent = '';
        contactLinkFor(addBasketBtn);
      }

      removeBtn.addEventListener('click', function () { remove(id); });

      var target = sections[sectionKey] && sections[sectionKey].grid;
      if (target) target.appendChild(card);
    });

    DUTY_SECTION_KEYS.forEach(function (key) {
      var s = sections[key];
      if (!s.section) return;
      s.section.hidden = !(s.grid && s.grid.children.length > 0);
    });

    refreshDutyState();
  }

  function renderWishlistPage() {
    var sections = dutySections();
    var empty = document.querySelector('[data-wishlist-empty]');
    var hasAnyGrid = DUTY_SECTION_KEYS.some(function (key) { return !!sections[key].grid; });
    if (!hasAnyGrid) return;

    if (currentIds.length === 0) {
      DUTY_SECTION_KEYS.forEach(function (key) {
        if (sections[key].grid) sections[key].grid.innerHTML = '';
        if (sections[key].section) sections[key].section.hidden = true;
      });
      if (empty) empty.style.display = '';
      return;
    }
    if (empty) empty.style.display = 'none';

    fetchWishlistItems(currentIds).then(renderCardsFromFeed);
  }

  // -- init --------------------------------------------------------------

  function init() {
    if (config.loggedIn) {
      fetchCustomerIds().then(function (serverIds) {
        currentIds = serverIds;
        wireButtons();
        syncUi();
        completePendingWishlistAdd();
      });
    } else {
      currentIds = [];
      wireButtons();
      syncUi();
    }
  }

  window.Wishlist = { isWishlisted: isWishlisted, toggle: toggle, remove: remove, setActiveVariant: setActiveVariant };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();